import { Link, createFileRoute } from '@tanstack/react-router';
import { CalendarSearch, History } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { EMPTY_ACTION_CLASS } from '@/components/emptyStateClasses';
import { ErrorBanner } from '@/components/ErrorBanner';
import { Field } from '@/components/Field';
import { Input } from '@/components/Input';
import { PageHeader } from '@/components/PageHeader';
import { Spinner } from '@/components/Spinner';
import { SyncBadge } from '@/components/workout/SyncBadge';
import {
  SessionListItem,
  entryFromLocal,
  entryFromSummary,
  type SessionListEntry,
} from '@/components/workout/SessionListItem';
import { DISCARD_SESSION } from '@/lib/i18n/training';
import {
  useCloseSession,
  useDiscardSession,
  useLocalSessions,
  useWorkoutHistory,
  type HistoryFilters,
} from '@/lib/queries/workouts';

export const Route = createFileRoute('/_app/train/history/')({
  component: HistoryPage,
});

/** ¿Entra la sesión en el rango elegido? Se compara por día, no por instante. */
function inRange(startedAt: string, { from, to }: HistoryFilters): boolean {
  const day = startedAt.slice(0, 10);
  if (from !== '' && day < from) return false;
  if (to !== '' && day > to) return false;
  return true;
}

function HistoryPage() {
  const fromId = useId();
  const toId = useId();
  const [filters, setFilters] = useState<HistoryFilters>({ from: '', to: '' });
  const history = useWorkoutHistory(filters);
  const local = useLocalSessions();
  const sentinel = useRef<HTMLDivElement | null>(null);
  const close = useCloseSession();
  const discard = useDiscardSession();
  // Lo descartado se oculta al momento: el borrado remoto va por la cola y el
  // listado del servidor tarda un poco en enterarse.
  const [discarded, setDiscarded] = useState<ReadonlySet<string>>(() => new Set());
  const [pending, setPending] = useState<{ clientId: string; action: 'finish' | 'discard' } | null>(
    null,
  );
  const actionError = close.error ?? discard.error;

  function finishEntry(clientId: string): void {
    setPending({ clientId, action: 'finish' });
    close.mutate(clientId, { onSettled: () => setPending(null) });
  }

  function discardEntry(clientId: string): void {
    setPending({ clientId, action: 'discard' });
    discard.mutate(clientId, {
      onSuccess: () => setDiscarded((current) => new Set(current).add(clientId)),
      onSettled: () => setPending(null),
    });
  }

  const { fetchNextPage, hasNextPage, isFetchingNextPage } = history;
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  // Scroll infinito con un centinela; el botón de abajo sigue estando por si el
  // observador no llega a dispararse (listas cortas, navegación por teclado).
  useEffect(() => {
    const node = sentinel.current;
    if (node === null) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore();
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [loadMore]);

  const entries = new Map<string, SessionListEntry>();
  for (const page of history.data?.pages ?? []) {
    for (const summary of page.items) entries.set(summary.clientId, entryFromSummary(summary));
  }
  for (const { session, sets } of local ?? []) {
    if (!inRange(session.startedAt, filters)) continue;
    // Lo que aún no ha subido no está en el listado del servidor; y si está,
    // gana la copia local mientras le queden series por sincronizar.
    if (session.syncedAt !== null && entries.has(session.clientId)) continue;
    if (session.syncedAt !== null && sets.every((set) => set.syncedAt !== null)) continue;
    entries.set(session.clientId, entryFromLocal(session, sets));
  }

  for (const clientId of discarded) entries.delete(clientId);
  const items = Array.from(entries.values()).sort((a, b) => b.startedAt.localeCompare(a.startedAt));
  const filtered = filters.from !== '' || filters.to !== '';

  return (
    <>
      <PageHeader title="Historial" subtitle="Todo lo que has entrenado, sesión a sesión." />

      <div className="flex flex-col gap-4">
        <SyncBadge />

        <Card title="Filtrar por fechas">
          <div className="flex gap-2">
            <Field htmlFor={fromId} label="Desde" className="flex-1">
              <Input
                id={fromId}
                type="date"
                value={filters.from}
                max={filters.to === '' ? undefined : filters.to}
                onChange={(event) =>
                  setFilters((current) => ({ ...current, from: event.target.value }))
                }
              />
            </Field>
            <Field htmlFor={toId} label="Hasta" className="flex-1">
              <Input
                id={toId}
                type="date"
                value={filters.to}
                min={filters.from === '' ? undefined : filters.from}
                onChange={(event) =>
                  setFilters((current) => ({ ...current, to: event.target.value }))
                }
              />
            </Field>
          </div>
          {filtered ? (
            <Button
              variant="ghost"
              className="mt-2"
              onClick={() => setFilters({ from: '', to: '' })}
            >
              Quitar el filtro
            </Button>
          ) : null}
        </Card>

        {history.isError ? (
          <ErrorBanner
            title="No hemos podido cargar el historial"
            error={history.error}
            onRetry={() => void history.refetch()}
          />
        ) : null}

        {actionError ? (
          <ErrorBanner
            title={close.error ? DISCARD_SESSION.finishError : DISCARD_SESSION.error}
            error={actionError}
          />
        ) : null}

        {history.isPending && local === undefined ? (
          <div className="flex justify-center py-10">
            <Spinner size="lg" />
          </div>
        ) : items.length === 0 ? (
          filtered ? (
            <EmptyState
              icon={CalendarSearch}
              title="No hay entrenamientos en esas fechas"
              description="Prueba con otro rango o quita el filtro para verlos todos."
              action={
                <Button variant="secondary" onClick={() => setFilters({ from: '', to: '' })}>
                  Quitar el filtro
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={History}
              title="Aún no has registrado ningún entrenamiento"
              description="Empieza un día de tu rutina o un entrenamiento libre. Cuando lo termines aparecerá aquí, con sus series y su volumen."
              action={
                <Link to="/train" className={EMPTY_ACTION_CLASS}>
                  Ir a Entrenar
                </Link>
              }
            />
          )
        ) : (
          <ul className="flex flex-col gap-1.5">
            {items.map((entry) => (
              <li key={entry.clientId}>
                <SessionListItem
                  entry={entry}
                  onFinish={() => finishEntry(entry.clientId)}
                  onDiscard={() => discardEntry(entry.clientId)}
                  pending={pending?.clientId === entry.clientId ? pending.action : null}
                />
              </li>
            ))}
          </ul>
        )}

        <div ref={sentinel} aria-hidden="true" />

        {history.hasNextPage ? (
          <Button variant="secondary" block loading={history.isFetchingNextPage} onClick={loadMore}>
            Cargar más
          </Button>
        ) : items.length > 0 ? (
          <p className="pb-4 text-center text-xs text-ink-faint">No hay nada más atrás.</p>
        ) : null}
      </div>
    </>
  );
}
